import type { FetchResult } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import {
    LoadingScreen,
    StyleFunction,
    Surface,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQPaymentConfigurationCreateMutation,
    usePaymentConfigurationCreateMutation,
    usePaymentConfigurationFormQuery,
} from "graphql-sdk";
import { IPaymentConfigurationInput, schemaPaymentConfiguration } from "lib";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { useHandleMutationError } from "../../hooks/use-handle-mutation-error";
import { RootStackParamList } from "../../navigation";
import { defaultPaymentConfiguration } from "./forms";
import { PaymentConfigurationForm } from "./payment-configuration-form";
import { PaymentConfigurationScreen } from "./payment-configuration-screen";

type ScreenProps = StackScreenProps<
    RootStackParamList,
    "PAYMENT_CONFIGURATION_CREATE"
>;

export function PaymentConfigurationCreateScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.payment_configuration.write");

    const [t] = useTranslation();
    const formData = usePaymentConfigurationFormQuery();
    const [paymentConfigurationCreate] =
        usePaymentConfigurationCreateMutation();
    const { handleMutationError } = useHandleMutationError();

    const styles = useThemedStyle(styleFunc);
    const form = useForm<IPaymentConfigurationInput>(
        schemaPaymentConfiguration,
        defaultPaymentConfiguration
    );
    const [{ values }] = form;

    const create = useCallback(
        async (paymentMethodOrder: string[]) => {
            if (!values) {
                return;
            }

            const sortedPaymentMethods = [...values.paymentMethods].sort(
                (a, b) =>
                    paymentMethodOrder.indexOf(a.methodId) -
                    paymentMethodOrder.indexOf(b.methodId)
            );

            let paymentConfig: FetchResult<
                GQPaymentConfigurationCreateMutation,
                Record<string, any>,
                Record<string, any>
            >;
            await handleMutationError(
                async () =>
                    (paymentConfig = await paymentConfigurationCreate({
                        variables: {
                            paymentConfiguration: {
                                name: values.name,
                                paymentMethods: sortedPaymentMethods,
                            },
                        },
                    })),
                t(
                    "backoffice.payment_configuration.created",
                    "Payment configuration created"
                ),
                () => {
                    if (
                        !paymentConfig.data ||
                        !paymentConfig.data.paymentConfigurationCreate ||
                        !paymentConfig.data.paymentConfigurationCreate.id
                    ) {
                        return;
                    }

                    // If the screen was loaded with a referrer, then forward to the edit with the referrer set
                    if (route.params && route.params.referrer.route) {
                        navigate("PAYMENT_CONFIGURATION_EDIT", {
                            id: paymentConfig.data.paymentConfigurationCreate
                                .id,
                            referrer: {
                                route: route.params.referrer.route,
                                id: route.params.referrer?.id,
                            },
                        });
                        return;
                    }

                    // There is no referrer, just redirect to the edit screen
                    navigate("PAYMENT_CONFIGURATION_EDIT", {
                        id: paymentConfig.data.paymentConfigurationCreate.id,
                    });
                }
            );
        },
        [
            values,
            handleMutationError,
            t,
            paymentConfigurationCreate,
            route,
            navigate,
        ]
    );

    if (!formData.data) {
        return <LoadingScreen style="light" />;
    }

    return (
        <PaymentConfigurationScreen>
            <Surface>
                <View style={styles.box}>
                    <View style={styles.container}>
                        <PaymentConfigurationForm
                            form={form}
                            paymentMethodOptions={formData.data}
                            initialSorting={formData.data.paymentMethods.data.map(
                                pm => pm.id
                            )}
                            onSubmit={create}
                            submitButton={["common.create", "Create"]}
                        />
                    </View>
                </View>
            </Surface>
        </PaymentConfigurationScreen>
    );
}

const styleFunc: StyleFunction = theme => ({
    box: {
        width: "100%",
        backgroundColor: theme.colors.white,
    },
    container: {
        width: "100%",
        maxWidth: 600,
        alignSelf: "flex-start",
    },
});
